/**
 * Horizontal slide carousel for the product gallery (Swiper-like behavior, no Swiper.js).
 * Mirrors the scroll-carousel API used by product-gallery, carousel-prev/next-button, and thumbnails.
 */
(() => {
  if (customElements.get('product-gallery-swiper')) return;

  const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ProductGallerySwiper extends HTMLElement {
    constructor() {
      super();
      /** @type {AbortController | null} */
      this._abort = null;
      /** @type {ResizeObserver | null} */
      this._ro = null;
      this._index = 0;
      this._transitioning = false;
    }

    get cellSelector() {
      return this.getAttribute('cell-selector') || '.product-gallery__media';
    }

    get allCells() {
      return Array.from(this.querySelectorAll(this.cellSelector));
    }

    get cells() {
      return this.allCells.filter((cell) => !cell.hasAttribute('hidden'));
    }

    get selectedCell() {
      return this.cells[this._index] ?? null;
    }

    get selectedIndex() {
      return this._index;
    }

    get groupCells() {
      return 1;
    }

    get adaptiveHeight() {
      return this.hasAttribute('adaptive-height');
    }

    get isScrollable() {
      return this.cells.length > 1;
    }

    get _viewport() {
      return this.querySelector('.product-gallery-swiper__viewport');
    }

    get _track() {
      return this.querySelector('.product-gallery-swiper__track');
    }

    /**
     * Visible width used for one slide (matches rendered viewport, including full-bleed on mobile).
     *
     * Rounded UP on purpose. The viewport is rarely a whole number of pixels — a 511.33px column with
     * Math.round would give 511px slides, leaving a third of a pixel at the right edge where the next
     * slide shows through as a thin vertical line. Rounding up makes each slide cover the viewport
     * completely, so exactly one image is ever visible.
     */
    _getSlideWidth() {
      const el = this._viewport ?? this;
      const rectW = el.getBoundingClientRect().width;
      if (rectW > 0) return Math.ceil(rectW);
      return el.clientWidth;
    }

    connectedCallback() {
      this._abort = new AbortController();
      const { signal } = this._abort;

      this._ro = new ResizeObserver(() => this._layout());
      this._ro.observe(this);
      if (this._viewport && this._viewport !== this) {
        this._ro.observe(this._viewport);
      }

      const initial = Math.max(0, this.cells.findIndex((c) => c.classList.contains('is-initial')));
      this._index = Math.min(initial, Math.max(0, this.cells.length - 1));

      this._layout();
      this._applyTransform(true);
      this._adaptHeight();
      queueMicrotask(() => {
        this._layout();
        this._applyTransform(true);
        this._onSettled();
      });

      this.addEventListener('transitionend', this._onTransitionEnd, { signal });
      if (this.cells.length > 1) {
        this.addEventListener('carousel:change', this._preloadNeighbors, { signal });
      }

      if (Shopify?.designMode) {
        this.addEventListener(
          'shopify:block:select',
          (e) => {
            const idx = this.cells.indexOf(e.target);
            if (idx >= 0) this.select(idx, { instant: e.detail?.load });
          },
          { signal }
        );
      }

      if (this.hasAttribute('allow-drag')) {
        const mq = window.matchMedia('screen and (pointer: fine)');
        const bindFinePointer = () => {
          if (mq.matches) {
            this._viewport?.addEventListener('mousedown', this._onMouseDown, { signal });
          } else {
            this._viewport?.removeEventListener('mousedown', this._onMouseDown);
          }
        };
        mq.addEventListener('change', bindFinePointer, { signal });
        bindFinePointer();
      }

      this._viewport?.addEventListener('touchstart', this._onTouchStart, { signal, passive: true });
      this._viewport?.addEventListener('touchmove', this._onTouchMove, { signal, passive: false });
      this._viewport?.addEventListener('touchend', this._onTouchEnd, { signal });
      this._viewport?.addEventListener('touchcancel', this._onTouchEnd, { signal });

      this._preloadNeighbors();
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._abort = null;
      this._ro?.disconnect();
      this._ro = null;
    }

    /**
     * @param {number[]} indexes — indices in `allCells` to hide (same contract as scroll-carousel)
     */
    filter(indexes = []) {
      this.allCells.forEach((cell, index) => {
        cell.toggleAttribute('hidden', indexes.includes(index));
      });
      this.dispatchEvent(new CustomEvent('carousel:filter', { detail: { filteredIndexes: indexes } }));

      if (!this.cells.length) {
        this._index = 0;
        this._applyTransform(true);
        return;
      }
      if (this._index >= this.cells.length) {
        this._index = this.cells.length - 1;
      }
      if (!this.cells[this._index]) {
        this._index = 0;
      }
      this._layout();
      this._applyTransform(true);
      this._adaptHeight();
      this._emitScrollEdges();
      this.dispatchEvent(
        new CustomEvent('carousel:settle', { detail: { index: this._index, cell: this.selectedCell } })
      );
    }

    previous({ instant = false } = {}) {
      this.select(Math.max(this._index - this.groupCells, 0), { instant });
    }

    next({ instant = false } = {}) {
      this.select(Math.min(this._index + this.groupCells, this.cells.length - 1), { instant });
    }

    /**
     * @param {number} index — index in visible `cells`
     * @param {{ instant?: boolean }} [opts]
     */
    select(index, { instant = false } = {}) {
      if (!(index in this.cells)) return;

      const cell = this.cells[index];
      this.dispatchEvent(new CustomEvent('carousel:select', { detail: { index, cell } }));

      if (index === this._index) {
        // Same index but transform may be wrong after a bounded drag overscroll — snap back.
        this._applyTransform(true);
        this._adaptHeight();
        return;
      }

      this._index = index;
      this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index, cell } }));

      const track = this._track;
      if (track) {
        if (instant || REDUCE_MOTION.matches) {
          track.classList.add('product-gallery-swiper__track--no-motion');
        } else {
          track.classList.remove('product-gallery-swiper__track--no-motion');
        }
      }

      this._transitioning = !instant && !REDUCE_MOTION.matches;
      this._applyTransform(instant || REDUCE_MOTION.matches);
      this._adaptHeight();

      if (instant || REDUCE_MOTION.matches) {
        this._onSettled();
      }
    }

    _readGapPx(track) {
      const g = getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0';
      const parsed = parseFloat(g);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    _layout() {
      const viewport = this._viewport;
      const track = this._track;
      if (!viewport || !track) return;

      const w = this._getSlideWidth();
      const n = this.cells.length;
      if (w <= 0 || n === 0) return;

      const gap = this._readGapPx(track);
      this.style.setProperty('--product-gallery-swiper-gap', `${gap}px`);
      this.style.setProperty('--product-gallery-swiper-viewport', `${w}px`);

      this.cells.forEach((cell) => {
        cell.style.flex = `0 0 ${w}px`;
        cell.style.width = `${w}px`;
        cell.style.maxWidth = `${w}px`;
      });

      track.style.width = `${n * w + Math.max(0, n - 1) * gap}px`;
      this._applyTransform(true);
    }

    _applyTransform(instant) {
      const track = this._track;
      const viewport = this._viewport;
      if (!track || !viewport) return;

      const w = this._getSlideWidth();
      const gap = this._readGapPx(track);
      const offset = this._index * (w + gap);
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const x = rtl ? offset : -offset;

      track.style.transform = `translate3d(${x}px, 0, 0)`;
      if (instant) {
        track.classList.add('product-gallery-swiper__track--no-motion');
        requestAnimationFrame(() => track.classList.remove('product-gallery-swiper__track--no-motion'));
      }
    }

    _adaptHeight() {
      if (!this.adaptiveHeight) {
        this.style.removeProperty('max-height');
        return;
      }
      const cell = this.selectedCell;
      if (!cell || !this.isScrollable) {
        this.style.removeProperty('max-height');
        return;
      }
      const h = cell.getBoundingClientRect().height;
      if (h > 0) this.style.maxHeight = `${Math.ceil(h)}px`;
    }

    _emitScrollEdges() {
      const last = this.cells.length - 1;
      if (this._index <= 0) {
        this.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'start' } }));
      } else {
        this.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'start' } }));
      }
      if (this._index >= last) {
        this.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'end' } }));
      } else {
        this.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'end' } }));
      }
    }

    _onTransitionEnd = (e) => {
      if (e.target !== this._track || e.propertyName !== 'transform') return;
      this._transitioning = false;
      this._onSettled();
    };

    _onSettled() {
      this.classList.toggle('is-scrollable', this.isScrollable);
      this._emitScrollEdges();
      this.dispatchEvent(
        new CustomEvent('carousel:settle', { detail: { index: this._index, cell: this.selectedCell } })
      );
    }

    _preloadNeighbors = () => {
      requestAnimationFrame(() => {
        const prev = this.cells[Math.max(this._index - 1, 0)];
        const next = this.cells[Math.min(this._index + 1, this.cells.length - 1)];
        const cur = this.selectedCell;
        [prev, cur, next]
          .filter(Boolean)
          .forEach((item) => {
            item.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
            item.querySelectorAll('video[preload="none"]').forEach((v) => v.setAttribute('preload', 'metadata'));
          });
      });
    };

    /**
     * Block the synthetic click after a drag so product-gallery lightbox (bubble listener on this node) does not open.
     */
    _suppressGhostClickAfterDrag() {
      const root = this;
      const onCaptureClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        root.removeEventListener('click', onCaptureClick, true);
        clearTimeout(timeoutId);
      };
      const timeoutId = setTimeout(() => {
        root.removeEventListener('click', onCaptureClick, true);
      }, 500);
      root.addEventListener('click', onCaptureClick, true);
    }

    _onMouseDown = (e) => {
      if (e.button !== 0 || !this.isScrollable) return;
      const track = this._track;
      if (!track) return;

      const startX = e.clientX;
      const startIndex = this._index;
      let moved = false;
      track.classList.add('product-gallery-swiper__track--no-motion');

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        this._setDragOffsetFromStart(startX, startIndex, dx);
      };

      const onUp = (ev) => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        track.classList.remove('product-gallery-swiper__track--no-motion');
        if (!moved) return;
        const dx = ev.clientX - startX;
        const w = this._getSlideWidth();
        const gap = this._track ? this._readGapPx(this._track) : 0;
        const stride = w + gap;
        let target = startIndex;
        if (stride > 0) {
          if (dx < -stride * 0.15) target = Math.min(startIndex + 1, this.cells.length - 1);
          else if (dx > stride * 0.15) target = Math.max(startIndex - 1, 0);
        }
        this.select(target, { instant: false });
        this._suppressGhostClickAfterDrag();
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };

    /**
     * Keep translate within real slides only (no empty space past first / last).
     * @param {number} x — translateX in px (same convention as _applyTransform)
     */
    _clampDragTranslateX(x) {
      const viewport = this._viewport;
      const track = this._track;
      if (!viewport || !track) return x;
      const w = this._getSlideWidth();
      const n = this.cells.length;
      if (n <= 1 || w <= 0) return x;
      const stride = w + this._readGapPx(track);
      const maxIndex = n - 1;
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      if (rtl) {
        const minX = 0;
        const maxX = maxIndex * stride;
        return Math.min(maxX, Math.max(minX, x));
      }
      const minX = -maxIndex * stride;
      const maxX = 0;
      return Math.min(maxX, Math.max(minX, x));
    }

    _setDragOffsetFromStart(startX, startIndex, dx) {
      const track = this._track;
      const viewport = this._viewport;
      if (!track || !viewport) return;
      const w = this._getSlideWidth();
      const gap = this._readGapPx(track);
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const base = startIndex * (w + gap);
      const dragShift = rtl ? dx : -dx;
      let x = rtl ? base + dragShift : -(base + dragShift);
      x = this._clampDragTranslateX(x);
      track.style.transform = `translate3d(${x}px, 0, 0)`;
    }

    /** @type {{ id: number, startX: number, startY: number, startIndex: number, moved: boolean, axis: 'x' | 'y' | null } | null} */
    _touch = null;

    _onTouchStart = (e) => {
      if (!this.isScrollable || !e.touches.length) return;
      const track = this._track;
      if (!track) return;
      this._touch = {
        id: e.touches[0].identifier,
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startIndex: this._index,
        moved: false,
        axis: null,
      };
    };

    _onTouchMove = (e) => {
      if (!this._touch || !e.touches.length) return;
      const t = [...e.touches].find((x) => x.identifier === this._touch.id);
      if (!t) return;

      const dx = t.clientX - this._touch.startX;
      const dy = t.clientY - this._touch.startY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      if (this._touch.axis === null) {
        if (absDx < 10 && absDy < 10) return;
        if (absDy > absDx) {
          this._touch.axis = 'y';
          return;
        }
        this._touch.axis = 'x';
        this._track?.classList.add('product-gallery-swiper__track--no-motion');
      }

      if (this._touch.axis === 'y') return;

      e.preventDefault();
      this._touch.moved = true;
      this._setDragOffsetFromStart(this._touch.startX, this._touch.startIndex, dx);
    };

    _onTouchEnd = () => {
      if (!this._touch) return;
      const { startIndex, moved, axis } = this._touch;
      const track = this._track;
      const viewport = this._viewport;
      this._touch = null;
      if (!track || !viewport) return;
      track.classList.remove('product-gallery-swiper__track--no-motion');
      if (axis === 'y' || !moved) {
        if (axis !== 'y') this.select(startIndex, { instant: true });
        return;
      }
      const w = this._getSlideWidth();
      const gap = this._readGapPx(track);
      const stride = w + gap;
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const style = getComputedStyle(track);
      const matrix = new DOMMatrixReadOnly(style.transform);
      const tx = matrix.m41;
      let raw = rtl ? tx / stride : -tx / stride;
      const maxI = this.cells.length - 1;
      raw = Math.min(maxI, Math.max(0, raw));
      const delta = raw - startIndex;
      let target = startIndex;
      if (delta > 0.15) target = Math.min(startIndex + 1, maxI);
      else if (delta < -0.15) target = Math.max(startIndex - 1, 0);
      this.select(target, { instant: false });
      this._suppressGhostClickAfterDrag();
    };
  }

  customElements.define('product-gallery-swiper', ProductGallerySwiper);
})();
